import React, { useEffect, useMemo, useState } from 'react';
import AuthService from '../../services/auth.service';
import MailService from '../../services/mail.service';
import { Card, CardHeader, CardBody, Row, Col, Button, FormGroup, Label, Input, Badge, Alert } from 'reactstrap';

export default function Mail() {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [useTemplate, setUseTemplate] = useState(true);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [attachmentMode, setAttachmentMode] = useState('single'); // 'single' or 'multiple'

  useEffect(() => {
    if (AuthService && typeof AuthService.getAll === 'function') {
      AuthService.getAll().then(res => {
        const arr = res.data || [];
        setUsers(arr);
      }).catch(() => setUsers([]));
    }
  }, []);

  const selectedUser = useMemo(() => users.find(u => String(u.id) === String(selectedUserId)), [users, selectedUserId]);

  const buildTemplateHtml = (contentHtml) => {
    // ... (keep your existing template code)
    return `<!doctype html>
<html>
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <title>All American Massage</title>
  <style>
    @media (max-width: 600px){ .container{ padding:16px !important; } }
    body{ margin:0; background:#f5f7fb; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji','Segoe UI Emoji','Segoe UI Symbol', sans-serif; color:#111827; }
    .wrapper{ width:100%; background:#f5f7fb; padding:24px 0; }
    .card{ max-width:680px; margin:0 auto; background:#ffffff; border-radius:16px; box-shadow:0 10px 30px rgba(17,24,39,.08); overflow:hidden; }
    .header{ background:linear-gradient(135deg, #34b5b8 0%, #51cbce 100%); padding:24px; text-align:center; }
    .brand{ display:inline-flex; align-items:center; gap:12px; color:#fff; text-decoration:none; }
    .brand img{ height:36px; width:auto; border-radius:6px; background:#ffffff14; padding:6px; }
    .brand h1{ margin:0; font-size:20px; letter-spacing:0.4px; }
    .container{ padding:32px 40px; }
    .title{ font-size:20px; font-weight:700; color:#0f172a; margin:0 0 12px; }
    .lead{ font-size:14px; color:#334155; line-height:1.7; margin:0 0 18px; }
    .content{ font-size:15px; color:#111827; line-height:1.8; }
    .content p{ margin:0 0 14px; }
    .divider{ height:1px; background:#e5e7eb; margin:28px 0; }
    .footer{ background:#0f172a; color:#cbd5e1; padding:24px; }
    .footer a{ color:#94a3b8; text-decoration:none; }
    .contact{ display:flex; flex-wrap:wrap; gap:16px; font-size:13px; }
    .contact div{ flex:1 1 220px; }
    .muted{ color:#64748b; font-size:12px; }
    .btn{ display:inline-block; padding:10px 16px; background:#51cbce; color:#0b1320; border-radius:10px; text-decoration:none; font-weight:600; box-shadow:0 6px 14px rgba(81,203,206,.3); }
  </style>
</head>
<body>
  <div class="wrapper">
    <div class="card">
      <div class="header">
        <a class="brand" href="https://allamericanmassagecouncil.com" target="_blank" rel="noopener">
          <h1>All American Massage</h1>
        </a>
      </div>
      <div class="container">
        <h2 class="title">Message from Support</h2>
        <p class="lead">We have an important message for you. Details are included below.</p>
        <div class="content">${contentHtml}</div>
        <div class="divider"></div>
        <p class="muted">If you have any questions, just reply to this email—we're here to help.</p>
      </div>
      <div class="footer">
        <div class="contact">
         
          <div>
            <strong>Contact</strong><br/>
            Email: support@allamericanmassagecouncil.com<br/>
            Web: <a href="https://app.allamericanmassagecouncil.com">allamericanmassagecouncil.com</a>
          </div>
          
        </div>
        <div style="margin-top:12px" class="muted">© ${new Date().getFullYear()} All American Massage. All rights reserved.</div>
      </div>
    </div>
  </div>
</body>
</html>`;
  };

  // Handle file selection for both single and multiple modes
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;

    if (attachmentMode === 'single') {
      // Replace all existing files with the new single file
      const newFile = {
        id: Date.now(),
        file: files[0],
        name: files[0].name,
        size: files[0].size,
        type: files[0].type
      };
      setAttachments([newFile]);
    } else {
      // Multiple mode: add new files to existing ones
      const newAttachments = files.map(file => ({
        id: Date.now() + Math.random(),
        file: file,
        name: file.name,
        size: file.size,
        type: file.type
      }));
      setAttachments(prev => [...prev, ...newAttachments]);
    }
    
    // Reset the input
    event.target.value = '';
  };

  // Handle drag and drop
  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    
    if (files.length === 0) return;

    if (attachmentMode === 'single') {
      const newFile = {
        id: Date.now(),
        file: files[0],
        name: files[0].name,
        size: files[0].size,
        type: files[0].type
      };
      setAttachments([newFile]);
    } else {
      const newAttachments = files.map(file => ({
        id: Date.now() + Math.random(),
        file: file,
        name: file.name,
        size: file.size,
        type: file.type
      }));
      setAttachments(prev => [...prev, ...newAttachments]);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  // Remove single file
  const handleRemoveFile = (id) => {
    setAttachments(attachments.filter(att => att.id !== id));
  };

  // Clear all files
  const handleClearAll = () => {
    setAttachments([]);
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get file icon based on type
  const getFileIcon = (fileType) => {
    if (fileType.includes('image')) return '🖼️';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('zip') || fileType.includes('archive')) return '📦';
    if (fileType.includes('video')) return '🎬';
    if (fileType.includes('audio')) return '🎵';
    return '📎';
  };

  const selectedFilesCount = attachments.length;
  const totalSize = attachments.reduce((sum, att) => sum + att.size, 0);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      setStatus({ type: 'error', msg: 'Select a user' });
      return;
    }
    if (!subject.trim() || !html.trim()) {
      setStatus({ type: 'error', msg: 'Subject and message are required' });
      return;
    }
    setSending(true);
    setStatus(null);
    try {
      const payloadHtml = useTemplate ? buildTemplateHtml(html) : html;
      const form = new FormData();
      form.append('to', selectedUser.email);
      form.append('subject', subject);
      form.append('html', payloadHtml);
      
      attachments.forEach((att) => {
        form.append('attachments', att.file);
      });
      
      await MailService.send(form);
      setStatus({ type: 'success', msg: 'Email sent successfully' });
      // Clear form after successful send
      setSelectedUserId('');
      setSubject('');
      setHtml('');
      setAttachments([]);
    } catch (err) {
      setStatus({ type: 'error', msg: err?.response?.data?.message || 'Failed to send email' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="content">
      <Row>
        <Col md="8">
          <Card>
            <CardHeader>
              <h5 className="title" style={{color: "white"}}>Send Email to User</h5>
            </CardHeader>
            <CardBody>
              {status && (
                <Alert color={status.type === 'success' ? 'success' : 'danger'}>
                  {status.msg}
                </Alert>
              )}
              
              <form onSubmit={handleSend} style={{color:"white"}}>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label for="user">Select User</Label>
                      <Input 
                        type="select" 
                        id="user" 
                        value={selectedUserId} 
                        onChange={(e) => setSelectedUserId(e.target.value)} 
                        required
                      >
                        <option value="">-- choose user --</option>
                        {users.map(u => (
                          <option key={u.id} value={u.id}>
                            {u.firstName || ''} {u.lastName || ''} ({u.email})
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="subject">Subject</Label>
                      <Input 
                        id="subject" 
                        value={subject} 
                        onChange={(e) => setSubject(e.target.value)} 
                        placeholder="Subject" 
                        required 
                      />
                    </FormGroup>
                  </Col>
                </Row>
                
                <Row>
                  <Col md="12">
                    <FormGroup check style={{ marginBottom: 12 }}>
                      <Label check>
                        <Input 
                          type="checkbox" 
                          checked={useTemplate} 
                          onChange={(e) => setUseTemplate(e.target.checked)} 
                        />
                        Wrap with branded template
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
                
                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label for="html">Message (HTML)</Label>
                      <Input 
                        id="html" 
                        type="textarea" 
                        rows={12} 
                        value={html} 
                        onChange={(e) => setHtml(e.target.value)} 
                        placeholder="<p>Your message...</p>" 
                        required 
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {/* Enhanced Attachments Section */}
                <Row>
                  <Col md="12">
                    <FormGroup>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <Label className="mb-0 fw-bold">Attachments</Label>
                        <div className="d-flex align-items-center gap-2">
                          {selectedFilesCount > 0 && (
                            <>
                              <Badge color="primary" pill>
                                {selectedFilesCount} file{selectedFilesCount !== 1 ? 's' : ''}
                              </Badge>
                              <Badge color="secondary" pill>
                                {formatFileSize(totalSize)}
                              </Badge>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Attachment Mode Selection */}
                      <div className="mb-3">
                        <div className="btn-group w-100" role="group" style={{padding: '20px', gap:30}}>
                          <button
                            type="button"
                            color={attachmentMode === 'single' ? 'primary' : 'outline-primary'}
                            onClick={() => {
                              setAttachmentMode('single');
                              if (attachments.length > 1) {
                                setAttachments(attachments.slice(0, 1));
                              }
                            }}
                            size="sm"
                            style={{width: '100%', background: '#34b3a0', padding: '10px', textAlign: 'center', height:'fit-content', textTransform: 'uppercase', color: '#fff'}}
                            
                            
                          >
                            Single File
                          </button>
                          <button
                            type="button"
                            color={attachmentMode === 'multiple' ? 'primary' : 'outline-primary'}
                            onClick={() => setAttachmentMode('multiple')}
                            size="sm"
                                                        style={{width: '100%', background: '#34b3a0', padding: '10px', textAlign: 'center', height:'fit-content', textTransform: 'uppercase', color: '#fff'}}

                          >
                            Multiple Files
                          </button>
                        </div>
                        <small className="text-muted mt-1 d-block">
                          {attachmentMode === 'single' 
                            ? 'Only one file can be attached at a time' 
                            : 'You can attach multiple files'}
                        </small>
                      </div>

                      {/* File Upload Area */}
                      <div
                        className="upload-area"
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        style={{
                          border: '2px dashed #dee2e6',
                          borderRadius: '0.75rem',
                          padding: '2rem',
                          textAlign: 'center',
                          backgroundColor: '#f8f9fa',
                          cursor: 'pointer',
                          marginBottom: '1rem',
                          transition: 'all 0.3s ease'
                        }}
                        onClick={() => document.getElementById('file-input').click()}
                      >
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📎</div>
                        <h6 className="text-dark">Drop files here or click to browse</h6>
                        <small className="text-muted">
                          {attachmentMode === 'single' 
                            ? 'Select a single file to attach' 
                            : 'Select multiple files to attach'}
                        </small>
                        <Input
                          id="file-input"
                          type="file"
                          multiple={attachmentMode === 'multiple'}
                          onChange={handleFileSelect}
                          style={{ display: 'none' }}
                          accept="*/*"
                        />
                      </div>

                      {/* Selected Files List */}
                      {attachments.length > 0 && (
                        <div className="selected-files-container">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="text-dark mb-0">Selected Files:</h6>
                            <Button
                              color="outline-danger"
                              size="sm"
                              onClick={handleClearAll}
                            >
                              Clear All
                            </Button>
                          </div>
                          
                          <div className="files-list">
                            {attachments.map((attachment) => (
                              <div
                                key={attachment.id}
                                className="file-item d-flex justify-content-between align-items-center p-2 mb-2"
                                style={{
                                  backgroundColor: 'white',
                                  borderRadius: '0.5rem',
                                  border: '1px solid #e9ecef'
                                }}
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <span style={{ fontSize: '1.25rem' }}>
                                    {getFileIcon(attachment.type)}
                                  </span>
                                  <div>
                                    <div className="text-dark small fw-bold">
                                      {attachment.name}
                                    </div>
                                    <div className="text-muted small">
                                      {formatFileSize(attachment.size)}
                                    </div>
                                  </div>
                                </div>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveFile(attachment.id);
                                  }}
                                  style={{ padding: '4px 8px' }}
                                  title="Remove file"
                                >
                                  ✕
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <small className="text-muted">
                        Supported: All file types • Max file size: 25MB each
                      </small>
                    </FormGroup>
                  </Col>
                </Row>

                <Button color="primary" type="submit" disabled={sending} className="mt-3">
                  {sending ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Sending...
                    </>
                  ) : (
                    'Send Email'
                  )}
                </Button>
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}